import { useState } from 'react'
import clsx from 'clsx'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { findCardById } from '@yadviga/shared'
import { CardLightbox } from './CardLightbox'

interface Props {
  cardId: string
  reversed: boolean
  revealed: boolean
  onReveal?: () => void
  /** Tailwind sizing utility for the card's bounding box (aspect-[2/3] is enforced). */
  className?: string
  /** Caption shown in the lightbox once the card is revealed and tapped. */
  positionLabel?: string
}

/**
 * Tarot card with a 3D flip animation. Both faces (deck back + card face) are
 * rendered simultaneously inside a `transform-style: preserve-3d` container;
 * `backface-visibility: hidden` ensures only the side facing the viewer paints.
 * Triggering `revealed=true` rotates the container 180° around Y over 700ms.
 *
 * Reversed cards add a 2D `rotate(180deg)` to the face image only — that
 * vertical flip is independent from the 3D Y-axis flip used for the reveal.
 *
 * Tap behaviour:
 *  • not revealed → call `onReveal` (flip the card)
 *  • revealed     → open a fullscreen lightbox so the artwork can be inspected
 */
export function TarotFlipCard({
  cardId,
  reversed,
  revealed,
  onReveal,
  className,
  positionLabel,
}: Props) {
  const { i18n } = useTranslation()
  const lang = i18n.language === 'en' ? 'en' : 'ru'
  const card = findCardById(cardId)
  const [showLightbox, setShowLightbox] = useState(false)
  if (!card) return null
  const facePath = `/cards/${card.imageKey}.webp`
  const cardName = lang === 'en' ? card.nameEn : card.nameRu

  const handleTap = () => {
    if (!revealed) {
      onReveal?.()
    } else {
      setShowLightbox(true)
    }
  }
  const tappable = revealed || Boolean(onReveal)

  return (
    <>
    <div
      onClick={tappable ? handleTap : undefined}
      className={clsx(
        'relative aspect-[2/3]',
        tappable && 'cursor-pointer transition-transform active:scale-95',
        className,
      )}
      style={{ perspective: '1200px' }}
    >
      <motion.div
        animate={{ rotateY: revealed ? 180 : 0 }}
        transition={{ duration: 0.7, ease: [0.4, 0, 0.2, 1] }}
        style={{ transformStyle: 'preserve-3d' }}
        className="absolute inset-0"
      >
        {/* Deck back — visible while not revealed */}
        <div
          style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' }}
          className="absolute inset-0 overflow-hidden rounded-2xl border border-sage-300/60 shadow-md"
        >
          <img
            src="/cards/card_back.webp"
            alt=""
            aria-hidden="true"
            className="h-full w-full object-cover"
            draggable={false}
          />
        </div>
        {/* Card face — pre-rotated 180° so it shows after the container flips */}
        <div
          style={{
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
          }}
          className="absolute inset-0 overflow-hidden rounded-2xl border border-sage-300/60 bg-cream shadow-md"
        >
          <img
            src={facePath}
            alt={cardName}
            className={clsx('h-full w-full object-cover', reversed && 'rotate-180')}
            draggable={false}
          />
        </div>
      </motion.div>
    </div>
    {showLightbox && (
      <CardLightbox
        card={card}
        reversed={reversed}
        imageUrl={facePath}
        positionLabel={positionLabel}
        onClose={() => setShowLightbox(false)}
      />
    )}
    </>
  )
}
